import { GoogleGenAI, Type } from "@google/genai";
import { VisualConceptResponse } from "../types";

// Initialize the client using process.env.API_KEY directly as required.
// The key is injected via vite.config.ts define property.
const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });

const SYSTEM_INSTRUCTION = `
Role: You are a Minimalist Social Media Illustrator. Your goal is to turn abstract user ideas into simple, high-impact visual metaphors in a hand-drawn style.

The Process:
1. Analyze Input: 
   - If provided text only: specific abstract concept to metaphor.
   - If provided text AND image: Use the image as a visual reference or starting point. Reinterpret the image's composition or subject matter into the requested "Ink on Paper" minimalist style, adapting it to match the text concept if present.
2. Conceptualize: Invent a visual metaphor that explains the idea (e.g., using ladders, batteries, mountains, simple charts, or juxtaposition).
3. Draft the Image Prompt: Write a highly descriptive prompt optimized for an image generator.

The Visual Style (Strict Adherence):
- Medium: Ink on Paper. It must look like a physical sketch, not vector art.
- Line Quality: Imperfect, organic, "wobbly" lines. Think "back of a napkin" or "whiteboard brainstorm."
- Colors: Strictly Black and White, with one intentional spot color (Electric Blue or Highlighter Yellow) used only to draw the eye to the focal point.
- Composition: centered, plenty of white space (negative space).
- Text: If text is necessary for the visual (like graph labels), keep it minimal and hand-printed.

Few-Shot Examples:
User Input: "The hardest step is the first one."
Reasoning: A simple staircase where the first step is comically taller than the rest.
JSON Output:
{
  "visual_concept": "A staircase where the first step is 5x higher than the others, representing the initial difficulty.",
  "image_generation_prompt": "A minimalist ink sketch on white paper. A side profile of a simple staircase. The very first step is exaggeratedly tall and difficult to climb, while the subsequent steps are normal size. A tiny stick figure stands at the bottom looking up at the huge first step. The figure is colored bright blue. The style is loose, hand-drawn with a fine-liner pen. Clean white background, high contrast."
}

User Input: "Don't trade your health for money."
Reasoning: A scale or balance. One side has a bag of money, the other has a heart/vitality.
JSON Output:
{
  "visual_concept": "A weighing scale tipping unfavorably.",
  "image_generation_prompt": "A simple hand-drawn sketch of an old-fashioned weighing scale. On the left side, a heavy bag labeled with a '$' sign is weighing it down. On the right side, up in the air, is a human heart. The heart is colored bright yellow. The lines are scratchy and organic, resembling a quick doodle in a notebook. Black ink on white paper."
}
`;

export const generateVisualConcept = async (
    userThought: string, 
    imagePart?: { inlineData: { data: string; mimeType: string } }
): Promise<VisualConceptResponse> => {
  try {
    const parts: any[] = [];
    
    // Add image if present
    if (imagePart) {
        parts.push(imagePart);
    }
    
    // Add text (defaulting to a generic prompt if empty but image is present)
    const textPrompt = userThought.trim() || "Create a minimalist visual metaphor based on this image.";
    parts.push({ text: textPrompt });

    const response = await ai.models.generateContent({
      model: 'gemini-2.5-flash',
      contents: { parts },
      config: {
        systemInstruction: SYSTEM_INSTRUCTION,
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            visual_concept: {
              type: Type.STRING,
              description: "A brief explanation of the metaphor chosen."
            },
            image_generation_prompt: {
              type: Type.STRING,
              description: "The optimized prompt for the image generator."
            }
          },
          required: ["visual_concept", "image_generation_prompt"]
        }
      }
    });

    const text = response.text;
    if (!text) throw new Error("No text response from Gemini");
    
    return JSON.parse(text) as VisualConceptResponse;
  } catch (error) {
    console.error("Error generating visual concept:", error);
    throw error;
  }
};

export const generateSketch = async (prompt: string): Promise<string> => {
  try {
    const response = await ai.models.generateImages({
      model: 'imagen-4.0-generate-001',
      prompt: prompt,
      config: {
        numberOfImages: 1,
        outputMimeType: 'image/jpeg',
        aspectRatio: '1:1', // Square for social media
      },
    });

    const generatedImage = response.generatedImages?.[0]?.image;
    
    if (!generatedImage || !generatedImage.imageBytes) {
        throw new Error("No image generated");
    }

    return `data:image/jpeg;base64,${generatedImage.imageBytes}`;
  } catch (error) {
    console.error("Error generating image:", error);
    throw error;
  }
};

export const modifySketch = async (imageBase64: string, instruction: string): Promise<string> => {
  try {
    // Detect mime type or default to jpeg
    const mimeMatch = imageBase64.match(/^data:(.+);base64,/);
    const mimeType = mimeMatch ? mimeMatch[1] : 'image/jpeg';
    // Remove data URL prefix
    const base64Data = imageBase64.replace(/^data:.+;base64,/, '');

    const response = await ai.models.generateContent({
      model: 'gemini-2.5-flash-image',
      contents: {
        parts: [
          {
            inlineData: {
              data: base64Data,
              mimeType: mimeType,
            },
          },
          {
            text: `Edit this image based on the following instruction: "${instruction}".
            
            CRITICAL STYLE GUIDELINES (MUST PRESERVE):
            - Style: Minimalist ink on paper sketch.
            - Line work: Imperfect, organic, "wobbly" lines (hand-drawn feel).
            - Color: Strictly Black and white with ONE spot color (Electric Blue or Highlighter Yellow) if appropriate.
            - Background: Clean white paper.
            - Do NOT produce a photorealistic image. Keep it looking like a sketch.`
          },
        ],
      },
    });

    // The response usually contains an inline image in the parts
    // Iterate through parts to find the image as per guidelines
    for (const part of response.candidates?.[0]?.content?.parts || []) {
      if (part.inlineData) {
        return `data:image/png;base64,${part.inlineData.data}`;
      }
    }
    
    throw new Error("No image generated from modification");
  } catch (error) {
    console.error("Error modifying sketch:", error);
    throw error;
  }
};